// CB-CONV §3.4 — INV-04. Integer minor units and an explicit currency. Never floats.
//
// Two rules that exist because margin figures must reconcile:
//   1. Rounding happens at exactly one point, `ExactMoney.round()`, called only when a line total is
//      finalised. Intermediate calculations carry full precision as rationals.
//   2. Percentage discounts and tax use rational arithmetic.
//
// The §3.4 sketch writes `multiply(factor: Rational): Money` with "no rounding here". A Money whose
// minor units are a bigint cannot hold an unrounded product, so `multiply` returns `ExactMoney` —
// rational minor units in the same currency — and `round()` lives there, once. That is the only
// shape in which both rules hold at the type level.
import {} from './currency.js';
import { Rational } from './rational.js';
export class CurrencyMismatchError extends Error {
    constructor(left, right) {
        super(`Currency mismatch: ${left} vs ${right}`);
        this.name = 'CurrencyMismatchError';
    }
}
function assertSameCurrency(left, right) {
    if (left !== right) {
        throw new CurrencyMismatchError(left, right);
    }
}
export class Money {
    minor;
    currency;
    constructor(minor, currency) {
        this.minor = minor;
        this.currency = currency;
    }
    static of(minor, currency) {
        return new Money(minor, currency);
    }
    static zero(currency) {
        return new Money(0n, currency);
    }
    add(other) {
        assertSameCurrency(this.currency, other.currency);
        return new Money(this.minor + other.minor, this.currency);
    }
    subtract(other) {
        assertSameCurrency(this.currency, other.currency);
        return new Money(this.minor - other.minor, this.currency);
    }
    negate() {
        return new Money(-this.minor, this.currency);
    }
    /** No rounding here. The result is exact; call `round()` once, when the line is finalised. */
    multiply(factor) {
        return ExactMoney.of(Rational.of(this.minor).multiply(factor), this.currency);
    }
    toExact() {
        return ExactMoney.of(Rational.of(this.minor), this.currency);
    }
    /**
     * Split by weights so the parts sum exactly to the whole. Remainder goes to the earliest parts,
     * one minor unit each, in order — deterministic, so the same split always gives the same parts.
     * Splitting $100.00 three ways yields 3334/3333/3333 rather than losing a cent.
     */
    allocate(weights) {
        if (weights.length === 0) {
            throw new RangeError('allocate needs at least one weight');
        }
        for (const w of weights) {
            if (!Number.isSafeInteger(w) || w < 0) {
                throw new RangeError(`Weights must be non-negative safe integers, got ${w}`);
            }
        }
        const total = weights.reduce((sum, w) => sum + BigInt(w), 0n);
        if (total === 0n) {
            throw new RangeError('Weights must not all be zero');
        }
        // Work in absolute value, then restore the sign, so a negative amount allocates symmetrically.
        const negative = this.minor < 0n;
        const magnitude = negative ? -this.minor : this.minor;
        const parts = weights.map((w) => (magnitude * BigInt(w)) / total);
        let remainder = magnitude - parts.reduce((sum, p) => sum + p, 0n);
        for (let i = 0; remainder > 0n && i < parts.length; i += 1) {
            if (weights[i] > 0) {
                parts[i] = parts[i] + 1n;
                remainder -= 1n;
            }
        }
        return parts.map((p) => new Money(negative ? -p : p, this.currency));
    }
    isZero() {
        return this.minor === 0n;
    }
    isNegative() {
        return this.minor < 0n;
    }
    compareTo(other) {
        assertSameCurrency(this.currency, other.currency);
        return this.minor < other.minor ? -1 : this.minor > other.minor ? 1 : 0;
    }
    equals(other) {
        return this.currency === other.currency && this.minor === other.minor;
    }
    toString() {
        return `${this.minor} ${this.currency}`;
    }
}
/** An amount mid-calculation: rational minor units. Only `round()` turns it back into Money. */
export class ExactMoney {
    minor;
    currency;
    constructor(minor, currency) {
        this.minor = minor;
        this.currency = currency;
    }
    static of(minor, currency) {
        return new ExactMoney(minor, currency);
    }
    add(other) {
        assertSameCurrency(this.currency, other.currency);
        const otherMinor = other instanceof Money ? Rational.of(other.minor) : other.minor;
        return new ExactMoney(this.minor.add(otherMinor), this.currency);
    }
    subtract(other) {
        assertSameCurrency(this.currency, other.currency);
        const otherMinor = other instanceof Money ? Rational.of(other.minor) : other.minor;
        return new ExactMoney(this.minor.subtract(otherMinor), this.currency);
    }
    multiply(factor) {
        return new ExactMoney(this.minor.multiply(factor), this.currency);
    }
    /**
     * The one rounding point in the money model. Call it when a line total is finalised and never
     * inside a loop: rounding each term then summing does not equal summing then rounding, and that is
     * how a quote and its invoice come to disagree.
     */
    round(mode = 'HALF_UP') {
        const { floor, remainder } = this.minor.floorDivMod();
        const half = Rational.of(1n, 2n);
        const cmp = remainder.compareTo(half);
        let result;
        if (cmp < 0) {
            result = floor;
        }
        else if (cmp > 0) {
            result = floor + 1n;
        }
        else if (mode === 'HALF_EVEN') {
            result = floor % 2n === 0n ? floor : floor + 1n;
        }
        else {
            // HALF_UP means half away from zero. floorDivMod gives floor toward -inf, so for a negative
            // value sitting exactly on a half, "away from zero" is the floor; for a positive one, floor+1.
            result = this.minor.isNegative() ? floor : floor + 1n;
        }
        return Money.of(result, this.currency);
    }
    toString() {
        return `${this.minor.toString()} ${this.currency} (exact)`;
    }
}
//# sourceMappingURL=money.js.map